import {
  differenceInMilliseconds,
  isToday as isTodayFns,
  isValid,
  parseISO,
} from 'date-fns';

export function getNextDay(isoDate: Date) {
  // clone the date over to avoid mutating the input
  const nextDay = new Date(isoDate.toString());
  nextDay.setDate(nextDay.getDate() + 1);
  return nextDay;
}

export function getPreviousDay(isoDate: Date) {
  // clone the date over to avoid mutating the input
  const previousDay = new Date(isoDate.toString());
  previousDay.setDate(previousDay.getDate() - 1);
  return previousDay;
}

/**
 * @description Takes a local date and returns a new date object with the time
 * set to noon UTC and the same year, month, and date as the input date.
 */
export function noonifyDate(localDate: Date) {
  const year = localDate.getFullYear();
  const month = localDate.getMonth();
  const date = localDate.getDate();

  if (year < 1900) {
    // JS doesn't handle historical dates in a way that makes sense for our
    // purposes, so we'll just return the original date.
    return localDate;
  }

  // create a new date object with the same year, month, and date, but with
  // the time set to noon
  return new Date(Date.UTC(year, month, date, 12, 0, 0));
}

/**
 * @description Check if a noonified date is "today" in the user's local time.
 * It ensures that even if the date is in UTC, it compares correctly with the local date.
 *
 * @param {Date} date - A noonified UTC date
 * @returns {boolean} True if the date is today in the local timezone, false otherwise.
 */
export function isTodayNoonified(date: Date): boolean {
  // Get the current local date
  const now = new Date();

  // Convert noonified UTC date back to local time
  const noonifiedLocal = new Date(
    date.getTime() + now.getTimezoneOffset() * 60000
  );

  // Use date-fns to check if the date (now converted to local time) is today
  return isTodayFns(noonifiedLocal);
}

/**
 * Checks if the two dates are greater than the specified number of hours apart.
 *
 * @param d1
 * @param d2
 * @param {number} hours
 * @returns {boolean} True if the two dates are greater than the specified number of hours apart, false otherwise.
 */
export function isTimeDifferenceGreaterThanXHours(
  d1: Date,
  d2: Date,
  hours: number
): boolean {
  const differenceInMs = Math.abs(differenceInMilliseconds(d1, d2));
  const hoursDifference = differenceInMs / (1000 * 60 * 60);
  return hoursDifference > hours;
}

/**
 * Parses a date of birth string to a Date object.
 * Supports "YYYY-MM-DD" and "YYYY/MM/DD" formats.
 *
 * @param dateString - The date string to parse
 * @returns Date object or null if parsing fails or input is falsey
 */
export function parseDateOfBirth(
  dateString: string | null | undefined
): Date | null {
  if (!dateString) return null;

  try {
    // First try to parse as ISO format (YYYY-MM-DD)
    let parsed = parseISO(dateString);
    if (isValid(parsed)) {
      return parsed;
    }

    // If that fails, try to handle YYYY/MM/DD format by converting to ISO
    const slashFormatMatch = dateString.match(
      /^(\d{4})\/(\d{1,2})\/(\d{1,2})$/
    );
    if (slashFormatMatch) {
      const [, year, month, day] = slashFormatMatch;
      if (year && month && day) {
        const isoFormatted = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        parsed = parseISO(isoFormatted);
        if (isValid(parsed)) {
          return parsed;
        }
      }
    }
  } catch {
    // parseISO or other operations failed, return null
  }

  return null;
}
